import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, KeyRound, Loader2, LogOut, RefreshCw, Search, ShieldCheck, Users, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/landing/Reveal";
import { api } from "@/lib/api";

const ACCESS_STYLES = {
    pending: "bg-amber-100 text-amber-700",
    granted: "bg-emerald-100 text-emerald-700",
    revoked: "bg-rose-100 text-rose-700",
};

const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState(null);
    const [search, setSearch] = useState("");
    const [accessFilter, setAccessFilter] = useState("");
    const [paymentFilter, setPaymentFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async (q = search, af = accessFilter, pf = paymentFilter) => {
        const params = {};
        if (q.trim()) params.search = q.trim();
        if (af) params.access_status = af;
        if (pf) params.payment_status = pf;
        const [ordersRes, statsRes] = await Promise.all([api.get("/admin/orders", { params }), api.get("/admin/stats")]);
        setOrders(ordersRes.data);
        setStats(statsRes.data);
    }, [search, accessFilter, paymentFilter]);

    useEffect(() => {
        api.get("/auth/me")
            .then(async ({ data }) => {
                setMe(data);
                await load("", "", "");
                setLoading(false);
            })
            .catch(() => navigate("/itf-control-panel"));
    }, [navigate, load]);

    useEffect(() => {
        if (!me) return;
        const t = setTimeout(() => load().catch(() => {}), 350);
        return () => clearTimeout(t);
    }, [search, accessFilter, paymentFilter, me, load]);

    const patchAccess = async (orderId, status) => {
        setBusyId(orderId);
        try {
            const { data } = await api.patch(`/admin/orders/${orderId}`, { access_status: status });
            setOrders((os) => os.map((o) => (o.order_id === orderId ? data : o)));
            const statsRes = await api.get("/admin/stats");
            setStats(statsRes.data);
            toast.success(`Access marked as ${status} for ${data.tradingview_username}`);
        } catch {
            toast.error("Update failed. Please try again.");
        } finally {
            setBusyId(null);
        }
    };

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        navigate("/itf-control-panel");
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#FAFBFD]">
                <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
            </div>
        );
    }

    const statCards = [
        { icon: Users, label: "Total orders", value: stats?.total_orders ?? 0 },
        { icon: Wallet, label: "Revenue", value: `$${((stats?.revenue_cents ?? 0) / 100).toLocaleString()}` },
        { icon: KeyRound, label: "Pending access", value: stats?.pending_access ?? 0 },
        { icon: ShieldCheck, label: "Access granted", value: stats?.granted_access ?? 0 },
    ];

    return (
        <div className="min-h-screen bg-[#FAFBFD]" data-testid="admin-dashboard-page">
            <header className="border-b border-slate-200/70 bg-white/85 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
                    <Link to="/"><Logo /></Link>
                    <div className="flex items-center gap-4">
                        <span className="hidden font-mono text-xs text-slate-500 sm:block">{me?.email}</span>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-button"
                            className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Log out
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
                <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Orders &amp; access</h1>
                <p className="mt-1 text-sm text-slate-500">Grant TradingView access after verifying each order.</p>

                <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats-grid">
                    {statCards.map((s) => (
                        <div key={s.label} className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-emerald-400">
                                <s.icon className="h-4 w-4" />
                            </span>
                            <p className="mt-3 font-display text-2xl font-extrabold tracking-tight text-slate-900">{s.value}</p>
                            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{s.label}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <div className="relative min-w-56 flex-1">
                        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            data-testid="admin-search-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, email, TradingView username, order ID…"
                            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                        />
                    </div>
                    <select
                        data-testid="admin-filter-access-select"
                        value={accessFilter}
                        onChange={(e) => setAccessFilter(e.target.value)}
                        className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-900"
                    >
                        <option value="">All access states</option>
                        <option value="pending">Pending</option>
                        <option value="granted">Granted</option>
                        <option value="revoked">Revoked</option>
                    </select>
                    <select
                        data-testid="admin-filter-payment-select"
                        value={paymentFilter}
                        onChange={(e) => setPaymentFilter(e.target.value)}
                        className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-900"
                    >
                        <option value="">All payments</option>
                        <option value="paid">Paid</option>
                        <option value="awaiting_payment">Awaiting payment</option>
                        <option value="refunded">Refunded</option>
                        <option value="failed">Failed</option>
                    </select>
                    <button
                        onClick={() => load()}
                        data-testid="admin-refresh-button"
                        className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                </div>

                <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm" data-testid="admin-dashboard-orders-table">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/60 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400">
                                    <th className="px-5 py-3.5 font-semibold">Customer</th>
                                    <th className="px-5 py-3.5 font-semibold">TradingView</th>
                                    <th className="px-5 py-3.5 font-semibold">Plan</th>
                                    <th className="px-5 py-3.5 font-semibold">Purchased</th>
                                    <th className="px-5 py-3.5 font-semibold">Expires</th>
                                    <th className="px-5 py-3.5 font-semibold">Access</th>
                                    <th className="px-5 py-3.5 font-semibold">Actions</th>
                                    <th className="px-5 py-3.5" />
                                </tr>
                            </thead>
                            <tbody>
                                {orders.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="px-5 py-14 text-center text-sm text-slate-400" data-testid="admin-orders-empty">
                                            No orders yet. New purchases appear here instantly.
                                        </td>
                                    </tr>
                                )}
                                {orders.map((o) => (
                                    <Fragment key={o.order_id}>
                                        <tr data-testid={`admin-order-row-${o.order_id}`} className="border-b border-slate-100 transition-colors hover:bg-slate-50/50">
                                            <td className="px-5 py-4">
                                                <p className="font-semibold text-slate-900">{o.full_name}</p>
                                                <p className="text-xs text-slate-500">{o.email}</p>
                                                <p className="mt-0.5 font-mono text-[10px] text-slate-400">{o.order_id}</p>
                                            </td>
                                            <td className="px-5 py-4 font-mono text-[13px] font-semibold text-slate-900">@{o.tradingview_username}</td>
                                            <td className="px-5 py-4">
                                                <p className="text-xs font-medium text-slate-700">{o.plan}</p>
                                                <p className="font-mono text-[11px] text-slate-400">
                                                    ${(o.amount_cents / 100).toFixed(2)} · {o.payment_status} · {o.payment_provider}
                                                </p>
                                                {o.dodo_payment_id && (
                                                    <p className="mt-0.5 font-mono text-[10px] text-slate-400">txn {o.dodo_payment_id}</p>
                                                )}
                                            </td>
                                            <td className="px-5 py-4 text-xs text-slate-600">{fmtDate(o.created_at)}</td>
                                            <td className="px-5 py-4 text-xs text-slate-600">{fmtDate(o.expires_at)}</td>
                                            <td className="px-5 py-4">
                                                <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${ACCESS_STYLES[o.access_status]}`}>
                                                    {o.access_status}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="flex gap-1.5">
                                                    {busyId === o.order_id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                                    ) : (
                                                        <>
                                                            {o.access_status !== "granted" && (
                                                                <button
                                                                    data-testid={`admin-access-grant-${o.order_id}`}
                                                                    onClick={() => patchAccess(o.order_id, "granted")}
                                                                    className="rounded-full bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-emerald-700"
                                                                >
                                                                    Grant
                                                                </button>
                                                            )}
                                                            {o.access_status === "granted" && (
                                                                <button
                                                                    data-testid={`admin-access-revoke-${o.order_id}`}
                                                                    onClick={() => patchAccess(o.order_id, "revoked")}
                                                                    className="rounded-full bg-rose-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-rose-700"
                                                                >
                                                                    Revoke
                                                                </button>
                                                            )}
                                                            {o.access_status === "revoked" && (
                                                                <button
                                                                    data-testid={`admin-access-pending-${o.order_id}`}
                                                                    onClick={() => patchAccess(o.order_id, "pending")}
                                                                    className="rounded-full bg-amber-500 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-amber-600"
                                                                >
                                                                    Set pending
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 py-4">
                                                <button
                                                    data-testid={`admin-history-toggle-${o.order_id}`}
                                                    onClick={() => setExpanded(expanded === o.order_id ? null : o.order_id)}
                                                    className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                                                >
                                                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded === o.order_id ? "rotate-180" : ""}`} />
                                                </button>
                                            </td>
                                        </tr>
                                        {expanded === o.order_id && (
                                            <tr className="border-b border-slate-100 bg-slate-50/60">
                                                <td colSpan={8} className="px-5 py-5">
                                                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                                        Order history {o.email_sent ? "· confirmation email sent" : ""}
                                                    </p>
                                                    <ol className="mt-3 space-y-2.5">
                                                        {o.history.map((h, i) => (
                                                            <li key={i} className="flex items-baseline gap-3 text-xs">
                                                                <span className="font-mono text-[10px] text-slate-400">
                                                                    {new Date(h.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                                                </span>
                                                                <span className="font-semibold text-slate-700">{h.event.replaceAll("_", " ")}</span>
                                                                <span className="text-slate-500">{h.detail}</span>
                                                            </li>
                                                        ))}
                                                    </ol>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                                            <p className="text-xs leading-relaxed text-slate-400">
                                                Access is granted manually inside TradingView (Manage Access on your published script), then marked as
                                                granted here. Payments are processed by Dodo Payments and confirmed via webhook — orders show as
                                                "awaiting_payment" until Dodo Payments confirms the transaction.
                                            </p>
            </main>
        </div>
    );
}
